/**
 * ComplianceManualPage - Compliance Manual Generation
 *
 * DRR-246/247: Survey-form-driven compliance content generation
 * Domain-agnostic: Manual type comes from tenant's active rulesets.
 *
 * Route: /ferp-core/compliance/manual
 */

import { useState } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  Button,
  Alert,
  ThemeIcon,
  Paper,
  Divider,
  Select,
  Loader,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconDownload,
  IconFileText,
  IconCheck,
  IconShield,
  IconClipboardList,
  IconSettings,
  IconBook,
} from '@tabler/icons-react';
import { useMutation, useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useTenantId } from '@/auth';

// =============================================================================
// GraphQL - Query available manual types from tenant's rulesets
// =============================================================================

const GET_AVAILABLE_MANUALS = gql`
  query GetAvailableManuals($tenantId: ID!) {
    rulesets(tenantId: $tenantId, filter: { hasManualTemplate: true }) {
      id
      displayName
      description
      sourceStatute
      checkpointCount
      manualTemplateId
    }
  }
`;

const GENERATE_COMPLIANCE_MANUAL = gql`
  mutation GenerateEpManual($tenantId: ID!, $rulesetId: String) {
    generateEpManual(tenantId: $tenantId, rulesetId: $rulesetId) {
      success
      documentId
      downloadUrl
      generatedAt
      rulesetId
      surveyForms
      checkpointsCovered
      taskGroups
      pageCount
      sizeBytes
      message
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

interface ManualType {
  id: string;
  displayName: string;
  description?: string;
  sourceStatute?: string;
  checkpointCount?: number;
  manualTemplateId?: string;
}

// Fallback manual types when query not available (bootstrap)
const DEFAULT_MANUAL_TYPES: ManualType[] = [
  {
    id: '0-3-001',
    displayName: 'Compliance Manual',
    description: 'Generated from active rulesets and survey checkpoints',
    checkpointCount: 26,
  },
];

// =============================================================================
// Component
// =============================================================================

export function ComplianceManualPage() {
  const tenantId = useTenantId();
  const [selectedRuleset, setSelectedRuleset] = useState<string | null>('0-3-001');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  // Query available manual types (graceful fallback if not implemented)
  const { data: manualsData, loading: manualsLoading } = useQuery(GET_AVAILABLE_MANUALS, {
    variables: { tenantId },
    skip: !tenantId,
    onError: () => {
      // Fallback to default - query may not be implemented yet
    },
  });

  const manualTypes: ManualType[] = manualsData?.rulesets?.length > 0
    ? manualsData.rulesets
    : DEFAULT_MANUAL_TYPES;

  const selectedManual = manualTypes.find(m => m.id === selectedRuleset) || manualTypes[0];

  const [generateManual, { loading, error, data }] = useMutation(GENERATE_COMPLIANCE_MANUAL, {
    onCompleted: (result) => {
      if (result?.generateEpManual?.downloadUrl) {
        setDownloadUrl(result.generateEpManual.downloadUrl);
      }
    },
  });

  const handleGenerate = () => {
    if (!tenantId || !selectedRuleset) return;
    generateManual({
      variables: {
        tenantId,
        rulesetId: selectedRuleset,
      },
    });
  };

  const handleDownload = () => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    }
  };

  const result = data?.generateEpManual;

  return (
    <Container size="lg" py="md">
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <Group gap="sm">
          <ThemeIcon size="lg" variant="light" color="blue">
            <IconBook size={20} />
          </ThemeIcon>
          <div>
            <Title order={3}>Compliance Manual</Title>
            <Text size="sm" c="dimmed">
              Generate documentation from regulatory source data
            </Text>
          </div>
        </Group>
        {selectedManual?.checkpointCount && (
          <Badge variant="light" color="green">
            {selectedManual.checkpointCount} checkpoints
          </Badge>
        )}
      </Group>

      <Stack gap="md">
        {/* Manual Selection */}
        {manualTypes.length > 1 && (
          <Card withBorder>
            <Group gap="md" align="flex-end">
              <Select
                label="Manual Type"
                description="Select the compliance manual to generate"
                data={manualTypes.map(m => ({ value: m.id, label: m.displayName }))}
                value={selectedRuleset}
                onChange={setSelectedRuleset}
                style={{ flex: 1 }}
                disabled={manualsLoading}
                rightSection={manualsLoading ? <Loader size="xs" /> : undefined}
              />
            </Group>
          </Card>
        )}

        {/* Info Card */}
        <Card withBorder>
          <Stack gap="sm">
            <Group gap="xs">
              <IconShield size={18} />
              <Text fw={500}>{selectedManual?.displayName || 'Compliance Manual'}</Text>
            </Group>
            <Text size="sm" c="dimmed">
              {selectedManual?.description ||
                'Generate your facility\'s compliance manual from regulatory source data. Content is derived from active rulesets and survey checkpoints.'}
            </Text>
            {selectedManual?.sourceStatute && (
              <>
                <Divider my="xs" />
                <Group gap="xl">
                  <Stack gap={4}>
                    <Text size="xs" c="dimmed">Source</Text>
                    <Badge color="blue">{selectedManual.sourceStatute}</Badge>
                  </Stack>
                  <Stack gap={4}>
                    <Text size="xs" c="dimmed">Checkpoints</Text>
                    <Badge color="green">{selectedManual.checkpointCount || 0} covered</Badge>
                  </Stack>
                  <Stack gap={4}>
                    <Text size="xs" c="dimmed">Ruleset</Text>
                    <Badge color="gray">{selectedRuleset}</Badge>
                  </Stack>
                </Group>
              </>
            )}
          </Stack>
        </Card>

        {/* Data Sources */}
        <Card withBorder>
          <Text fw={500} mb="sm">Data Sources</Text>
          <Group gap="md">
            <Paper withBorder p="sm" style={{ flex: 1 }}>
              <Group gap="xs" mb="xs">
                <IconFileText size={16} />
                <Text size="sm" fw={500}>Ruleset</Text>
              </Group>
              <Text size="xs" c="dimmed">
                Regulatory requirements with interpretive guidance
              </Text>
            </Paper>
            <Paper withBorder p="sm" style={{ flex: 1 }}>
              <Group gap="xs" mb="xs">
                <IconSettings size={16} />
                <Text size="sm" fw={500}>Constellation</Text>
              </Group>
              <Text size="xs" c="dimmed">
                Facility contacts, partners, configuration
              </Text>
            </Paper>
            <Paper withBorder p="sm" style={{ flex: 1 }}>
              <Group gap="xs" mb="xs">
                <IconClipboardList size={16} />
                <Text size="sm" fw={500}>Task Groups</Text>
              </Group>
              <Text size="xs" c="dimmed">
                Operational tasks and checklists
              </Text>
            </Paper>
          </Group>
        </Card>

        {/* Error Display */}
        {error && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            Failed to generate manual: {error.message}
          </Alert>
        )}

        {/* Success Display */}
        {result?.success && (
          <Alert color="green" icon={<IconCheck size={16} />}>
            <Group justify="space-between" align="center">
              <div>
                <Text fw={500}>Manual Generated Successfully</Text>
                <Text size="sm">
                  {result.pageCount} pages, {Math.round((result.sizeBytes || 0) / 1024)} KB
                  {result.checkpointsCovered && (
                    <>{' '}&bull;{' '}{result.checkpointsCovered} checkpoints covered</>
                  )}
                </Text>
              </div>
              <Button
                leftSection={<IconDownload size={16} />}
                onClick={handleDownload}
                color="green"
              >
                Download PDF
              </Button>
            </Group>
          </Alert>
        )}

        {/* Generate Button */}
        <Card withBorder>
          <Group justify="space-between" align="center">
            <div>
              <Text fw={500}>Generate Manual</Text>
              <Text size="sm" c="dimmed">
                Creates a PDF document with all required compliance content
              </Text>
            </div>
            <Button
              leftSection={<IconFileText size={16} />}
              onClick={handleGenerate}
              loading={loading}
              disabled={!tenantId || !selectedRuleset}
              size="md"
            >
              {loading ? 'Generating...' : 'Generate Manual'}
            </Button>
          </Group>
        </Card>

        {/* Footer Note */}
        <Text size="xs" c="dimmed" ta="center">
          Content derived from regulatory source data (public domain).
          Regenerate when requirements change.
        </Text>
      </Stack>
    </Container>
  );
}

export default ComplianceManualPage;
